import axios, { AxiosRequestHeaders, AxiosInstance, AxiosHeaders } from "axios";
import {
  ClientResponse,
  SupplierResponse,
  SupplierInfoResponse,
  AddPropertyRequest,
  ClientPropertyResponse,
  ClientContentResponse,
  AddRegionRequest,
  ClientRegionResponse,
  UpdateClientRegionRequest,
} from "../types/client";
import { IAutoComplete } from "../types/autoComplete";
import {
  SubClientListResponse,
  SubClientResponse,
  SubClientUpdateRequest,
  SubClientCreateRequest,
  LanguageResponse,
  CurrencyResponse,
} from "../types/subClient";
import {
  PaymentMethodTemplateResponse,
  CreatePaymentTemplateRequest,
  UpdatePaymentTemplateRequest,
  InputType,
} from "../types/payment";
import {
  ClientPaymentMethodResponse,
  CreateClientPaymentMethodRequest,
  UpdateClientPaymentMethodRequest,
} from "../types/clientPayment";
import {
  ClientFilterRequest,
  FilterRequest,
  PageableRequest,
  PageResponse,
} from "../types/pagination";
import {
  IndexStatusResponse,
  PropertyIndexStatusResponse,
} from "../types/indexStatus";


class Api {
  private backofficeApi: AxiosInstance;
  private autocompleteApi: AxiosInstance;
  private indexApi: AxiosInstance;
  private headers: AxiosRequestHeaders;

  constructor() {
    this.headers = new AxiosHeaders();
    this.headers.set("Content-Type", "application/json;charset=utf-8");
    this.headers.set("Cache-Control", "no-cache");

    this.backofficeApi = axios.create({
      baseURL: process.env.REACT_APP_API_BACKOFFICE,
      timeout: 40000,
      headers: this.headers,
    });
    this.autocompleteApi = axios.create({
      baseURL: process.env.REACT_APP_API_AUTOCOMPLETE,
      timeout: 40000,
      headers: this.headers,
    });

    this.indexApi = axios.create({
      baseURL: process.env.REACT_APP_API_STATIC_INDEXER,
      timeout: 40000,
      headers: this.headers,
    });

    this.backofficeApi.interceptors.request.use((config) => {
      const token = localStorage.getItem("tbi_access_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.autocompleteApi.interceptors.request.use((config) => {
      const token = localStorage.getItem("tbi_access_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.backofficeApi.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  // Client methods
  async getAllClients(): Promise<ClientResponse[]> {
    const response = await this.backofficeApi.get<ClientResponse[]>(
      "/api/clients"
    );
    return response.data;
  }

  async getClientById(id: string): Promise<ClientResponse> {
    const response = await this.backofficeApi.get<ClientResponse>(
      `/api/clients/${id}`
    );
    return response.data;
  }

  async createClient(client: ClientResponse): Promise<ClientResponse> {
    const response = await this.backofficeApi.post<ClientResponse>(
      "/api/clients/create",
      client
    );
    return response.data;
  }

  async updateClient(client: ClientResponse): Promise<ClientResponse> {
    const response = await this.backofficeApi.put<ClientResponse>(
      "/api/clients/update",
      client
    );
    return response.data;
  }

  async deleteClient(id: string): Promise<void> {
    await this.backofficeApi.delete(`/api/clients/${id}`);
  }

  // Client pageable methods
  async getClientsPageable(
    request: PageableRequest & FilterRequest
  ): Promise<PageResponse<ClientResponse>> {
    const params = new URLSearchParams();

    // Add filters
    Object.entries(request).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        key !== "page" &&
        key !== "size" &&
        key !== "sort"
      ) {
        params.append(key, value.toString());
      }
    });
    const response = await this.backofficeApi.get<PageResponse<ClientResponse>>(
      `/api/clients?${params}`
    );
    return response.data;
  }

  async getClientsPageableWithFilters(
    request: PageableRequest & ClientFilterRequest
  ): Promise<PageResponse<ClientResponse>> {
    const params = new URLSearchParams();

    // Add filters
    Object.entries(request).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        key !== "page" &&
        key !== "size" &&
        key !== "sort"
      ) {
        params.append(key, value.toString());
      }
    });
    const response = await this.backofficeApi.get<PageResponse<ClientResponse>>(
      `/api/clients?${params}`
    );
    return response.data;
  }

  // Supplier methods
  async getAllSuppliers(): Promise<SupplierResponse[]> {
    const response = await this.backofficeApi.get<SupplierResponse[]>(
      "/api/suppliers"
    );
    return response.data;
  }

  async getSupplierById(id: string): Promise<SupplierResponse> {
    const response = await this.backofficeApi.get<SupplierResponse>(
      `/api/suppliers/${id}`
    );
    return response.data;
  }

  async createSupplier(
    supplier: Omit<SupplierResponse, "id" | "creationDate" | "modificationDate">
  ): Promise<SupplierResponse> {
    const response = await this.backofficeApi.post<SupplierResponse>(
      "/api/suppliers",
      supplier
    );
    return response.data;
  }

  async updateSupplier(supplier: SupplierResponse): Promise<SupplierResponse> {
    const response = await this.backofficeApi.put<SupplierResponse>(
      "/api/suppliers/update",
      supplier
    );
    return response.data;
  }

  async deleteSupplier(id: string): Promise<void> {
    await this.backofficeApi.delete(`/api/suppliers/${id}`);
  }

  // Client Content methods
  async addPropertyToClient(
    clientId: string,
    request: AddPropertyRequest
  ): Promise<ClientPropertyResponse> {
    const response = await this.backofficeApi.post<ClientPropertyResponse>(
      `/api/client-content/properties/create?clientId=${clientId}`,
      request
    );
    return response.data;
  }

  async removePropertyFromClient(
    clientId: string,
    propertyId: string
  ): Promise<void> {
    await this.backofficeApi.delete(
      `/api/client-content/properties/${propertyId}?clientId=${clientId}`
    );
  }

  async getClientProperties(
    clientId: string
  ): Promise<ClientPropertyResponse[]> {
    const response = await this.backofficeApi.get<ClientPropertyResponse[]>(
      `/api/client-content/properties?clientId=${clientId}`
    );
    return response.data;
  }

  async addRegionToClient(
    clientId: string,
    request: AddRegionRequest
  ): Promise<ClientRegionResponse> {
    const response = await this.backofficeApi.post<ClientRegionResponse>(
      `/api/client-content/regions/create?clientId=${clientId}`,
      request
    );
    return response.data;
  }

  async removeRegionFromClient(
    clientId: string,
    regionId: string
  ): Promise<void> {
    await this.backofficeApi.delete(
      `/api/client-content/regions/${regionId}?clientId=${clientId}`
    );
  }

  async getClientRegions(clientId: string): Promise<ClientRegionResponse[]> {
    const response = await this.backofficeApi.get<ClientRegionResponse[]>(
      `/api/client-content/regions?clientId=${clientId}`
    );
    return response.data;
  }

  async getClientContent(clientId: string): Promise<ClientContentResponse> {
    const response = await this.backofficeApi.get<ClientContentResponse>(
      `/api/client-content/all?clientId=${clientId}`
    );
    return response.data;
  }

  async autoComplete(word: string, language: string, clientId: string) {
    return await this.autocompleteApi.post<IAutoComplete[]>(
      `/tbi/fullName`,
      {
        mainTerm: word,
        returnLimit: 10,
      },
      {
        headers: {
          language: language,
          "x-clientId": clientId,
        },
      }
    );
  }

  async getSubClientByUrl(url: string, raw: boolean = false): Promise<SubClientResponse> {
    const params = `url=${encodeURIComponent(url)}${raw ? '&raw=true' : ''}`;
    const response = await this.backofficeApi.get<SubClientResponse>(
      `/api/sub-clients/by-url?${params}`
    );
    return response.data;
  }



  async getSubClientsByClientId(
    clientId: string
  ): Promise<SubClientListResponse[]> {
    const response = await this.backofficeApi.get<SubClientListResponse[]>(
      `/api/sub-clients/by-client/${clientId}`
    );
    return response.data;
  }

  async getAllLanguages(): Promise<LanguageResponse[]> {
    const response = await this.backofficeApi.get<LanguageResponse[]>(
      "/api/sub-clients/languages"
    );
    return response.data;
  }

  async getAllCurrencies(): Promise<CurrencyResponse[]> {
    const response = await this.backofficeApi.get<CurrencyResponse[]>(
      "/api/sub-clients/currencies"
    );
    return response.data;
  }

  // Currency Management
  async getCurrencies(): Promise<CurrencyResponse[]> {
    const response = await this.backofficeApi.get<CurrencyResponse[]>("/currencies");
    return response.data;
  }

  async getCurrencyById(id: string): Promise<CurrencyResponse> {
    const response = await this.backofficeApi.get<CurrencyResponse>(`/currencies/${id}`);
    return response.data;
  }

  async createCurrency(currency: Omit<CurrencyResponse, 'id'>): Promise<CurrencyResponse> {
    const response = await this.backofficeApi.post<CurrencyResponse>("/currencies", currency);
    return response.data;
  }

  async updateCurrency(id: string, currency: Omit<CurrencyResponse, 'id'>): Promise<CurrencyResponse> {
    const response = await this.backofficeApi.put<CurrencyResponse>(`/currencies/${id}`, currency);
    return response.data;
  }

  async deleteCurrency(id: string): Promise<void> {
    await this.backofficeApi.delete(`/currencies/${id}`);
  }

  // Language Management
  async getLanguages(): Promise<LanguageResponse[]> {
    const response = await this.backofficeApi.get<LanguageResponse[]>("/languages");
    return response.data;
  }

  async getLanguageById(id: string): Promise<LanguageResponse> {
    const response = await this.backofficeApi.get<LanguageResponse>(`/languages/${id}`);
    return response.data;
  }

  async createLanguage(language: Omit<LanguageResponse, 'id'>): Promise<LanguageResponse> {
    const response = await this.backofficeApi.post<LanguageResponse>("/languages", language);
    return response.data;
  }

  async updateLanguage(id: string, language: Omit<LanguageResponse, 'id'>): Promise<LanguageResponse> {
    const response = await this.backofficeApi.put<LanguageResponse>(`/languages/${id}`, language);
    return response.data;
  }

  async deleteLanguage(id: string): Promise<void> {
    await this.backofficeApi.delete(`/languages/${id}`);
  }

  async createSubClient(
    request: SubClientCreateRequest
  ): Promise<SubClientListResponse> {
    const response = await this.backofficeApi.post<SubClientListResponse>(
      "/api/sub-clients/create",
      request
    );
    return response.data;
  }

  async updateSubClient(
    request: SubClientUpdateRequest
  ): Promise<SubClientListResponse> {
    const response = await this.backofficeApi.put<SubClientListResponse>(
      "/api/sub-clients/update",
      request
    );
    return response.data;
  }

  async deleteSubClient(id: string): Promise<void> {
    await this.backofficeApi.delete(`/api/sub-clients/${id}`);
  }

  // Footer I18n methods
  async getFooterI18n(subClientId: string): Promise<any[]> {
    const response = await this.backofficeApi.get(
      `/api/sub-clients/footer-i18n/${subClientId}`
    );
    return response.data;
  }

  async saveFooterI18n(request: any): Promise<any> {
    const response = await this.backofficeApi.post(
      `/api/sub-clients/footer-i18n`,
      request
    );
    return response.data;
  }

  async deleteFooterI18n(subClientId: string, languageId: string): Promise<void> {
    await this.backofficeApi.delete(
      `/api/sub-clients/footer-i18n/${subClientId}/${languageId}`
    );
  }

  async previewFooter(request: any): Promise<any> {
    const response = await this.backofficeApi.post(
      `/api/sub-clients/footer/preview`,
      request
    );
    return response.data;
  }

  async updateClientRegion(
    request: UpdateClientRegionRequest
  ): Promise<ClientRegionResponse> {
    const response = await this.backofficeApi.put<ClientRegionResponse>(
      "/api/client-content/regions/update",
      request
    );
    return response.data;
  }

  // Payment Template methods
  async getAllPaymentTemplates(): Promise<PaymentMethodTemplateResponse[]> {
    const response = await this.backofficeApi.get<
      PaymentMethodTemplateResponse[]
    >("/api/admin/payment-templates");
    return response.data;
  }

  async getPaymentTemplateById(
    id: string
  ): Promise<PaymentMethodTemplateResponse> {
    const response =
      await this.backofficeApi.get<PaymentMethodTemplateResponse>(
        `/api/admin/payment-templates/${id}`
      );
    return response.data;
  }

  async createPaymentTemplate(
    request: CreatePaymentTemplateRequest
  ): Promise<PaymentMethodTemplateResponse> {
    const response =
      await this.backofficeApi.post<PaymentMethodTemplateResponse>(
        "/api/admin/payment-templates",
        request
      );
    return response.data;
  }

  async updatePaymentTemplate(
    id: string,
    request: UpdatePaymentTemplateRequest
  ): Promise<PaymentMethodTemplateResponse> {
    const response =
      await this.backofficeApi.put<PaymentMethodTemplateResponse>(
        `/api/admin/payment-templates/${id}`,
        request
      );
    return response.data;
  }

  async deletePaymentTemplate(id: string): Promise<void> {
    await this.backofficeApi.delete(`/api/admin/payment-templates/${id}`);
  }
  // ❌ Removed: getPaymentInputNames endpoint (no longer exists)

  async getInputTypes(): Promise<InputType[]> {
    const response = await this.backofficeApi.get<InputType[]>(
      "/api/admin/payment-templates/enums/input-types"
    );
    return response.data;
  }

  // Client Payment methods
  async getClientPaymentMethods(
    clientId: string
  ): Promise<ClientPaymentMethodResponse[]> {
    const response = await this.backofficeApi.get<
      ClientPaymentMethodResponse[]
    >(`/api/admin/clients/${clientId}/payment-methods`);
    return response.data;
  }

  async getClientPaymentMethodById(
    clientId: string,
    id: string
  ): Promise<ClientPaymentMethodResponse> {
    const response = await this.backofficeApi.get<ClientPaymentMethodResponse>(
      `/api/admin/clients/${clientId}/payment-methods/${id}`
    );
    return response.data;
  }

  async createClientPaymentMethod(
    clientId: string,
    request: CreateClientPaymentMethodRequest
  ): Promise<ClientPaymentMethodResponse> {
    const response = await this.backofficeApi.post<ClientPaymentMethodResponse>(
      `/api/admin/clients/${clientId}/payment-methods`,
      request
    );
    return response.data;
  }

  async updateClientPaymentMethod(
    clientId: string,
    id: string,
    request: UpdateClientPaymentMethodRequest
  ): Promise<ClientPaymentMethodResponse> {
    const response = await this.backofficeApi.put<ClientPaymentMethodResponse>(
      `/api/admin/clients/${clientId}/payment-methods/${id}`,
      request
    );
    return response.data;
  }

  async deleteClientPaymentMethod(clientId: string, id: string): Promise<void> {
    await this.backofficeApi.delete(
      `/api/admin/clients/${clientId}/payment-methods/${id}`
    );
  }

  // Supplier Info Management
  async createSupplierInfo(clientId: string, supplierInfo: Omit<SupplierInfoResponse, 'id'>): Promise<SupplierInfoResponse> {
    const response = await this.backofficeApi.post<SupplierInfoResponse>(
      `/api/clients/create/supplier-info`,
      supplierInfo,
      {
        headers: {
          'x-clientId': clientId
        }
      }
    );
    return response.data;
  }

  async updateSupplierInfo(clientId: string, supplierInfo: SupplierInfoResponse): Promise<SupplierInfoResponse> {
    const response = await this.backofficeApi.put<SupplierInfoResponse>(
      `/api/clients/update/supplier-info`,
      supplierInfo,
      {
        headers: {
          'x-clientId': clientId
        }
      }
    );
    return response.data;
  }

  // Reindexing Methods
  async reindexClient(clientId: string): Promise<void> {
    await this.backofficeApi.put(
      `/api/clients/index/reindex`,
      {},
      {
        headers: {
          'x-clientId': clientId
        }
      }
    );
  }

  async reindexSupplier(supplierId: string): Promise<void> {
    await this.backofficeApi.put(
      `/api/suppliers/index/reindex`,
      {},
      {
        headers: {
          'x-supplierId': supplierId
        }
      }
    );
  }

  // Content Control Methods
  async updateBlockedProperties(clientId: string, supplierInfoId: string, ids: string[]): Promise<void> {
    await this.backofficeApi.put(
      `/api/clients/blocked/update/properties`,
      ids,
      {
        headers: {
          'x-clientId': clientId,
          'x-supplierInfoId': supplierInfoId
        }
      }
    );
  }

  async updateAllowedProperties(clientId: string, supplierInfoId: string, ids: string[]): Promise<void> {
    await this.backofficeApi.put(
      `/api/clients/allowed/update/properties`,
      ids,
      {
        headers: {
          'x-clientId': clientId,
          'x-supplierInfoId': supplierInfoId
        }
      }
    );
  }

  async updateBlockedRegions(clientId: string, supplierInfoId: string, ids: string[]): Promise<void> {
    await this.backofficeApi.put(
      `/api/clients/blocked/update/regions`,
      ids,
      {
        headers: {
          'x-clientId': clientId,
          'x-supplierInfoId': supplierInfoId
        }
      }
    );
  }

  async updateAllowedRegions(clientId: string, supplierInfoId: string, ids: string[]): Promise<void> {
    await this.backofficeApi.put(
      `/api/clients/allowed/update/regions`,
      ids,
      {
        headers: {
          'x-clientId': clientId,
          'x-supplierInfoId': supplierInfoId
        }
      }
    );
  }

  // Supplier Payment Methods
  async getSupplierPaymentMethods(
    supplierId: string
  ): Promise<ClientPaymentMethodResponse[]> {
    const response = await this.backofficeApi.get<
      ClientPaymentMethodResponse[]
    >(`/api/admin/suppliers/${supplierId}/payment-methods`);
    return response.data;
  }

  async createSupplierPaymentMethod(
    supplierId: string,
    request: CreateClientPaymentMethodRequest
  ): Promise<ClientPaymentMethodResponse> {
    const response = await this.backofficeApi.post<ClientPaymentMethodResponse>(
      `/api/admin/suppliers/${supplierId}/payment-methods`,
      request
    );
    return response.data;
  }

  async updateSupplierPaymentMethod(
    supplierId: string,
    paymentMethodId: string,
    request: UpdateClientPaymentMethodRequest
  ): Promise<ClientPaymentMethodResponse> {
    const response = await this.backofficeApi.put<ClientPaymentMethodResponse>(
      `/api/admin/suppliers/${supplierId}/payment-methods/${paymentMethodId}`,
      request
    );
    return response.data;
  }

  async deleteSupplierPaymentMethod(
    supplierId: string,
    paymentMethodId: string
  ): Promise<void> {
    await this.backofficeApi.delete(
      `/api/admin/suppliers/${supplierId}/payment-methods/${paymentMethodId}`
    );
  }

  // Index Status methods
  async getIndexStatus(): Promise<IndexStatusResponse> {
    const response = await this.indexApi.get("/regions/index/information");
    return response.data;
  }

  async getPropertyIndexStatus(): Promise<PropertyIndexStatusResponse> {
    const response = await this.indexApi.get("/property/index/information");
    return response.data;
  }

  // New Kafka-based endpoints
  async getKafkaRegionInformation(): Promise<any> {
    const response = await this.indexApi.get("/regions/kafka/index/information");
    return response.data;
  }

  async getKafkaPropertyInformation(): Promise<any> {
    const response = await this.indexApi.get("/property/kafka/index/information");
    return response.data;
  }

  async getAllTasks(): Promise<any> {
    const response = await this.indexApi.get("/kafka/reports/tasks/all");
    return response.data;
  }

  async getTaskDetails(taskId: string): Promise<any> {
    const response = await this.indexApi.get(`/kafka/reports/task/${taskId}`);
    return response.data;
  }

  async getKafkaOverview(): Promise<any> {
    const response = await this.indexApi.get("/kafka/reports/overview");
    return response.data;
  }

  async getKafkaHealthStatus(): Promise<any> {
    const response = await this.indexApi.get("/kafka/reports/health");
    return response.data;
  }

  async getQueueStatus(): Promise<any> {
    const response = await this.indexApi.get("/kafka/management/status");
    return response.data;
  }

  // Task management
  async startRegionIndexing(language: string = 'en-US'): Promise<any> {
    const response = await this.indexApi.put("/regions/kafka/index/all", {}, {
      headers: { language }
    });
    return response.data;
  }

  async startPropertyIndexing(language: string = 'en-US'): Promise<any> {
    const response = await this.indexApi.put("/property/kafka/index/all", {}, {
      headers: { language }
    });
    return response.data;
  }

  async startPropertyIndexingByCountry(countryCode: string, language: string): Promise<any> {
    const response = await this.indexApi.put(`/property/kafka/index/${countryCode}`, {}, {
      headers: { language }
    });
    return response.data;
  }

  async cancelTask(taskId: string): Promise<any> {
    const response = await this.indexApi.post(`/kafka/management/tasks/cancel/${taskId}`);
    return response.data;
  }

  async cancelAllTasks(): Promise<any> {
    const response = await this.indexApi.post("/kafka/management/tasks/cancel-all");
    return response.data;
  }

  async pauseQueue(queueType: string): Promise<any> {
    const response = await this.indexApi.post(`/kafka/management/pause/${queueType}`);
    return response.data;
  }

  async resumeQueue(queueType: string): Promise<any> {
    const response = await this.indexApi.post(`/kafka/management/resume/${queueType}`);
    return response.data;
  }
}

export default Api;
